import React from 'react';

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

type CardProps = React.HTMLAttributes<HTMLDivElement> & {
  interactive?: boolean;
};

export const Card: React.FC<CardProps> = ({ className, interactive = false, ...props }) => (
  <div className={cx(interactive ? 'iv-card-interactive' : 'iv-card', className)} {...props} />
);

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => <div className={cx('mb-4', className)} {...props} />;

export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className, ...props }) => (
  <div className={cx('space-y-3', className)} {...props} />
);

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  fullWidth?: boolean;
};

const buttonVariantClass: Record<ButtonVariant, string> = {
  primary: 'iv-btn-primary',
  secondary: 'iv-btn-secondary',
  danger: 'iv-btn-danger',
  ghost: 'iv-btn-ghost',
};

export const Button: React.FC<ButtonProps> = ({
  className,
  variant = 'primary',
  fullWidth = false,
  ...props
}) => (
  <button
    className={cx(buttonVariantClass[variant], fullWidth ? 'w-full' : '', className)}
    {...props}
  />
);

type LabelProps = React.LabelHTMLAttributes<HTMLLabelElement>;
export const Label: React.FC<LabelProps> = ({ className, ...props }) => (
  <label className={cx('iv-label', className)} {...props} />
);

type InputProps = React.InputHTMLAttributes<HTMLInputElement>;
export const Input: React.FC<InputProps> = ({ className, ...props }) => (
  <input className={cx('iv-input', className)} {...props} />
);

type SelectProps = React.SelectHTMLAttributes<HTMLSelectElement>;
export const Select: React.FC<SelectProps> = ({ className, ...props }) => (
  <select className={cx('iv-input', className)} {...props} />
);

type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement>;
export const Textarea: React.FC<TextareaProps> = ({ className, ...props }) => (
  <textarea className={cx('iv-input', className)} {...props} />
);

type BadgeVariant = 'brand' | 'copper' | 'danger';
const badgeVariantClass: Record<BadgeVariant, string> = {
  brand: 'iv-badge-brand',
  copper: 'iv-badge-copper',
  danger: 'iv-badge-danger',
};

type BadgeProps = React.HTMLAttributes<HTMLSpanElement> & {
  variant?: BadgeVariant;
};

export const Badge: React.FC<BadgeProps> = ({ className, variant = 'brand', ...props }) => (
  <span className={cx(badgeVariantClass[variant], className)} {...props} />
);

type AvatarProps = React.HTMLAttributes<HTMLDivElement> & {
  initials: string;
};

export const Avatar: React.FC<AvatarProps> = ({ initials, className, ...props }) => (
  <div
    className={cx(
      'w-9 h-9 rounded-avatar flex items-center justify-center text-[11px] font-bold bg-brand-50 text-brand-600',
      className
    )}
    {...props}
  >
    {initials}
  </div>
);

type TabItem = {
  id: string;
  label: string;
  count?: number;
};

type TabNavProps = {
  tabs: TabItem[];
  activeTab: string;
  onChange: (tabId: string) => void;
  className?: string;
};

export const TabNav: React.FC<TabNavProps> = ({ tabs, activeTab, onChange, className }) => (
  <div className={cx('iv-tab-nav', className)}>
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={cx(
          'px-5 py-3.5 min-h-[44px] whitespace-nowrap flex-shrink-0 text-[13px] font-semibold border-b-2 -mb-[2px] transition-all duration-200',
          activeTab === tab.id
            ? 'text-brand-600 border-brand-600'
            : 'text-txt-muted border-transparent hover:text-txt-secondary'
        )}
        type="button"
      >
        {tab.label}
        {typeof tab.count === 'number' ? (
          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-brand-50 text-brand-600">
            {tab.count}
          </span>
        ) : null}
      </button>
    ))}
  </div>
);

type DataTableCell = {
  value: React.ReactNode;
  type?: 'positive' | 'negative';
};

type DataTableProps = {
  headers: string[];
  rows: DataTableCell[][];
  className?: string;
};

export const DataTable: React.FC<DataTableProps> = ({ headers, rows, className }) => (
  <div className={cx('overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0', className)}>
    <div className="min-w-[540px] iv-table-wrap">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="bg-surface-header">
            {headers.map((header) => (
              <th key={header} className="iv-table-head-cell">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              key={`row-${rowIndex}`}
              className={cx(
                rowIndex % 2 === 0 ? 'bg-surface-card' : 'bg-surface-alt',
                'hover:bg-surface-hover transition-colors duration-150 border-b border-line-subtle last:border-b-0'
              )}
            >
              {row.map((cell, colIndex) => (
                <td
                  key={`cell-${rowIndex}-${colIndex}`}
                  className={cx(
                    'px-4 py-3',
                    colIndex === 0 ? 'font-medium text-txt-primary' : 'text-txt-secondary',
                    cell.type === 'positive' ? 'text-success font-semibold' : '',
                    cell.type === 'negative' ? 'text-danger font-semibold' : ''
                  )}
                >
                  {cell.value}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  className?: string;
};

export const Modal: React.FC<ModalProps> = ({ open, onClose, title, children, className }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center md:justify-center">
      <button
        type="button"
        className="absolute inset-0 bg-surface-overlay"
        onClick={onClose}
        aria-label="Close modal"
      />
      <div
        className={cx(
          'relative w-full md:w-auto md:max-w-lg bg-surface-card rounded-t-[20px] md:rounded-card shadow-modal p-4 md:p-[22px] max-h-[92vh] overflow-y-auto',
          className
        )}
      >
        <div className="md:hidden w-10 h-1 bg-line rounded-full mx-auto mt-1 mb-4" />
        {title ? <h3 className="text-section-title font-display font-bold text-txt-primary mb-3">{title}</h3> : null}
        {children}
      </div>
    </div>
  );
};

